import * as React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { gql, useClient } from "urql";
import { GOOGLE_DRIVE_OAUTH_STORAGE_KEYS } from "~/config/google-drive/oauth-callback";
import { Box } from "~/design-system/Box";
import { Button } from "~/design-system/Button";
import { Card } from "~/design-system/Card";
import { Form } from "~/design-system/Form";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text";
import { TextField } from "~/design-system/TextField";
import { FullPageSpinner } from "../FullPageSpinner/FullPageSpinner";

const CompleteProjectDriveAuthDocument = gql`
  query CompleteProjectDriveAuth($code: String!, $state: String!) {
    completeProjectDriveAuth(code: $code, state: $state)
  }
`;

const ImportProjectDriveStructureDocument = gql`
  mutation ImportProjectDriveStructure(
    $driveFolderId: String!
    $projectId: uuid!
  ) {
    importProjectDriveStructure(
      driveFolderId: $driveFolderId
      projectId: $projectId
    )
  }
`;

const DRIVE_FOLDER_ID_REGEX = /\/folders\/([a-zA-Z0-9_-]+)/;
const DRIVE_FOLDER_ALREADY_LINKED_ERROR_CODE =
  "GOOGLE_DRIVE_FOLDER_ALREADY_LINKED_TO_ANOTHER_PROJECT";

const clearStoredDriveOAuthCallbackParams = () => {
  Object.values(GOOGLE_DRIVE_OAUTH_STORAGE_KEYS).forEach((key) => {
    window.sessionStorage.removeItem(key);
  });
};

const extractDriveFolderId = (rawValue: string) => {
  const value = rawValue.trim();
  if (!value) {
    return null;
  }

  const fromUrl = value.match(DRIVE_FOLDER_ID_REGEX)?.[1];
  if (fromUrl) {
    return fromUrl;
  }

  // Accept direct folder id input for faster manual testing.
  return value;
};

function GoogleDriveCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useClient();
  const [searchParams] = useSearchParams();
  const hasStartedRef = React.useRef(false);
  const [isCompletingOAuth, setIsCompletingOAuth] = React.useState(true);
  const [isImporting, setIsImporting] = React.useState(false);
  const [projectId, setProjectId] = React.useState<string | null>(null);
  const [driveFolderInput, setDriveFolderInput] = React.useState("");
  const [driveFolderInputError, setDriveFolderInputError] = React.useState<
    string | undefined
  >();

  React.useEffect(() => {
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    let cancelled = false;

    const hashParams = new URLSearchParams(
      window.location.hash.replace(/^#/, "")
    );
    const code =
      searchParams.get("code") ??
      hashParams.get("code") ??
      window.sessionStorage.getItem(GOOGLE_DRIVE_OAUTH_STORAGE_KEYS.code);
    const state =
      searchParams.get("state") ??
      hashParams.get("state") ??
      window.sessionStorage.getItem(GOOGLE_DRIVE_OAUTH_STORAGE_KEYS.state);
    const oauthError =
      searchParams.get("error") ??
      hashParams.get("error") ??
      window.sessionStorage.getItem(GOOGLE_DRIVE_OAUTH_STORAGE_KEYS.error);

    console.info("[DriveIntegration] Callback URL received", {
      codeExists: Boolean(code),
      errorExists: Boolean(oauthError),
      pathname: window.location.pathname,
      stateExists: Boolean(state),
    });

    if (oauthError) {
      clearStoredDriveOAuthCallbackParams();
      toast.error(
        t(
          "screens.Project.Infos.InfosScreenActions.googleDriveOauthDenied",
          "Google Drive authorization was cancelled."
        )
      );
      navigate("/dashboard", { replace: true });
      setIsCompletingOAuth(false);
      return;
    }

    if (!code || !state) {
      clearStoredDriveOAuthCallbackParams();
      console.error("[DriveIntegration] Missing OAuth callback params", {
        codeExists: Boolean(code),
        pathname: window.location.pathname,
        stateExists: Boolean(state),
      });
      toast.error(
        t(
          "screens.Project.Infos.InfosScreenActions.googleDriveOauthMissingParams",
          "Missing OAuth callback parameters."
        )
      );
      navigate("/dashboard", { replace: true });
      setIsCompletingOAuth(false);
      return;
    }

    void client
      .query<
        { completeProjectDriveAuth: string | null },
        { code: string; state: string }
      >(CompleteProjectDriveAuthDocument, { code, state })
      .toPromise()
      .then((response) => {
        if (cancelled) {
          return;
        }

        clearStoredDriveOAuthCallbackParams();
        if (response.error || !response.data?.completeProjectDriveAuth) {
          console.error("[DriveIntegration] OAuth callback completion failed", {
            graphQLErrors: response.error?.graphQLErrors,
            message: response.error?.message,
            networkError: response.error?.networkError,
            responseData: response.data,
          });
          toast.error(
            t(
              "screens.Project.Infos.InfosScreenActions.googleDriveOauthCompleteError",
              "Unable to complete Drive integration."
            )
          );
          navigate("/dashboard", { replace: true });
          return;
        }

        toast.success(
          t(
            "screens.Project.Infos.InfosScreenActions.googleDriveOauthCompleteSuccess",
            "Drive integration connected successfully."
          )
        );
        setProjectId(response.data.completeProjectDriveAuth);
      })
      .catch((unexpected: unknown) => {
        if (cancelled) {
          return;
        }
        clearStoredDriveOAuthCallbackParams();
        console.error("[DriveIntegration] OAuth callback unexpected error", {
          error:
            unexpected instanceof Error
              ? unexpected.message
              : String(unexpected),
        });
        toast.error(
          t(
            "screens.Project.Infos.InfosScreenActions.googleDriveOauthCompleteError",
            "Unable to complete Drive integration."
          )
        );
        navigate("/dashboard", { replace: true });
      })
      .finally(() => {
        if (!cancelled) {
          setIsCompletingOAuth(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [client, navigate, searchParams, t]);

  const skipImportForNow = () => {
    if (!projectId) {
      navigate("/dashboard", { replace: true });
      return;
    }
    navigate(`/project/${projectId}/info`, { replace: true });
  };

  const runDriveImport = async (driveFolderId: string, projectId: string) => {
    setIsImporting(true);

    try {
      const importResponse = await client
        .mutation<
          { importProjectDriveStructure: string | null },
          { driveFolderId: string; projectId: string }
        >(ImportProjectDriveStructureDocument, { driveFolderId, projectId })
        .toPromise();

      if (
        importResponse.error ||
        !importResponse.data?.importProjectDriveStructure
      ) {
        const hasFolderAlreadyLinkedError =
          importResponse.error?.graphQLErrors.some(
            (graphQLError) =>
              graphQLError.extensions?.code ===
              DRIVE_FOLDER_ALREADY_LINKED_ERROR_CODE
          );
        console.error("[DriveIntegration] Drive structure import failed", {
          driveFolderId,
          graphQLErrors: importResponse.error?.graphQLErrors,
          hasFolderAlreadyLinkedError,
          message: importResponse.error?.message,
          networkError: importResponse.error?.networkError,
          projectId,
          responseData: importResponse.data,
        });
        if (hasFolderAlreadyLinkedError) {
          toast.error(
            t(
              "screens.Project.Infos.InfosScreenActions.driveFolderAlreadyLinkedToAnotherProject",
              "This Google Drive folder is already linked to another project."
            )
          );
          return;
        }
        toast.error(
          t(
            "screens.Project.Infos.InfosScreenActions.driveImportError",
            "Drive connected, but structure import failed."
          )
        );
        return;
      }

      toast.success(
        t(
          "screens.Project.Infos.InfosScreenActions.driveImportSuccess",
          "Drive connected and structure imported successfully."
        )
      );
      navigate(`/project/${projectId}`, { replace: true });
    } catch (unexpected) {
      console.error("[DriveIntegration] Drive import unexpected error", {
        driveFolderId,
        error:
          unexpected instanceof Error ? unexpected.message : String(unexpected),
        projectId,
      });
      toast.error(
        t(
          "screens.Project.Infos.InfosScreenActions.driveImportError",
          "Drive connected, but structure import failed."
        )
      );
    } finally {
      setIsImporting(false);
    }
  };

  const importStructure = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!projectId) {
      navigate("/dashboard", { replace: true });
      return;
    }

    const driveFolderId = extractDriveFolderId(driveFolderInput);
    if (!driveFolderId) {
      setDriveFolderInputError(
        t(
          "screens.Project.Infos.InfosScreenActions.driveFolderInvalid",
          "Please provide a valid Google Drive folder URL or folder ID."
        )
      );
      return;
    }
    setDriveFolderInputError(undefined);

    await runDriveImport(driveFolderId, projectId);
  };

  if (isCompletingOAuth) {
    return <FullPageSpinner />;
  }

  if (!projectId) {
    return <FullPageSpinner />;
  }

  return (
    <Box
      css={{
        alignItems: "center",
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        left: 0,
        padding: "$space$4",
        position: "fixed",
        right: 0,
        top: 0,
        zIndex: 1,
      }}
    >
      <Card
        width="full"
        maxWidth="xsmall"
        padding="large"
        css={{
          margin: "0 auto",
        }}
      >
        <Form onSubmit={importStructure}>
          <Stack space="small">
            <Text weight="strong" size="large">
              {t(
                "screens.Project.Infos.InfosScreenActions.driveFolderSelectionTitle",
                "Select a Google Drive folder"
              )}
            </Text>
            <Text tone="secondary" size="small">
              {t(
                "screens.Project.Infos.InfosScreenActions.driveFolderSelectionDescription",
                "Paste a folder URL or folder ID to import the structure into Clovis."
              )}
            </Text>
            <TextField
              autoFocus
              dataClovisActionId="drive-folder-selection-input"
              name="drive-folder-id"
              label={t(
                "screens.Project.Infos.InfosScreenActions.driveFolderInputLabel",
                "Google Drive folder URL or ID"
              )}
              placeholder={t(
                "screens.Project.Infos.InfosScreenActions.driveFolderInputPlaceholder",
                "https://drive.google.com/drive/folders/..."
              )}
              value={driveFolderInput}
              onChange={(event) => {
                setDriveFolderInput(event.target.value);
                if (driveFolderInputError) {
                  setDriveFolderInputError(undefined);
                }
              }}
              error={driveFolderInputError}
            />
            <Stack space="xsmall">
              <Button
                type="submit"
                variant="solid"
                tone="brandAccent"
                width="full"
                loading={isImporting}
              >
                {t(
                  "screens.Project.Infos.InfosScreenActions.driveImportAction",
                  "Import structure"
                )}
              </Button>
              <Button
                type="button"
                variant="soft"
                tone="neutral"
                width="full"
                onClick={skipImportForNow}
              >
                {t(
                  "screens.Project.Infos.InfosScreenActions.driveImportSkip",
                  "Skip for now"
                )}
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Card>
    </Box>
  );
}

export { GoogleDriveCallback };
